import { appendMissingParentInputs } from './parentInputs.js'
import { resolveDomainEventOrDecision, buildEventOrDecisionRef } from './workflowSpec.js'
import {
  computeStableOffsets,
  reconcileEventGroupIds,
  recalculateGroupSlots,
  buildSvgGraph,
  reorderEventsToLongestPath
} from './diagramLayout.js'

const isLoopBackParent = (parentId, targetId, eventMap) => {
  const visited = new Set()
  const stack = [parentId]
  while (stack.length > 0) {
    const currentId = stack.pop()
    if (currentId === targetId) {
      return true
    }
    if (currentId === 'start' || visited.has(currentId)) {
      continue
    }
    visited.add(currentId)
    for (const next of eventMap.get(currentId)?.parents || []) {
      stack.push(next)
    }
  }

  return false
}

const findDecisionBetween = (sourceId, targetEvent, eventMap) => {
  for (const parentId of targetEvent.parents || []) {
    if (eventMap.get(parentId)?.type !== 'bpmn:ExclusiveGateway') {
      continue
    }
    const visited = new Set()
    const stack = [parentId]
    while (stack.length > 0) {
      const currentId = stack.pop()
      if (visited.has(currentId)) {
        continue
      }
      visited.add(currentId)
      for (const ancestorId of eventMap.get(currentId)?.parents || []) {
        if (ancestorId === sourceId) {
          return eventMap.get(parentId)
        }
        if (eventMap.get(ancestorId)?.type === 'bpmn:ExclusiveGateway') {
          stack.push(ancestorId)
        }
      }
    }
  }

  return null
}

const applyStartNodeRule = (targetId, rawParents, eventMap) => {
  const realParents = rawParents.filter((parentId) => parentId !== 'start')
  if (realParents.length === 0) {
    return ['start']
  }
  if (realParents.every((parentId) => isLoopBackParent(parentId, targetId, eventMap))) {
    return ['start', ...realParents]
  }

  return realParents
}

const buildConnectionMutationInput = ({
  workflow,
  workflowId,
  targetEvent,
  targetIndex,
  parents,
  conditionLabels,
  action
}) => {
  const domainEvents = workflow.domainEvents || []
  const groups = workflow.groups || []

  const simulatedEvents = domainEvents.map((event) =>
    event.id === targetEvent.id ? { ...event, parents } : event
  )

  // Reorder each event's parents[0] to its longest-path parent, exactly as the frontend does
  // on every diagram refresh. Columns are derived from parents[0], so persisting the reordered
  // order is what pushes a multi-parent event to the right of its new parent instead of leaving
  // it anchored to a shorter parent and rendered as a backward edge. The reflow mutates its own
  // svgGraph/svgGroups clones; we only read the reordered parents back out.
  const reorderedEvents = simulatedEvents.map((event) => ({ ...event, parents: [...(event.parents || [])] }))
  const { svgGraph, svgGroups } = buildSvgGraph({ domainEvents: reorderedEvents, lanes: workflow.lanes, groups })
  reorderEventsToLongestPath(svgGraph, svgGroups)
  const originalParentsById = new Map(domainEvents.map((event) => [event.id, event.parents || []]))
  const reorderedParentsById = new Map(reorderedEvents.map((event) => [event.id, event.parents]))
  const targetParents = reorderedParentsById.get(targetEvent.id) || parents

  const reconciliationUpdates = reconcileEventGroupIds(reorderedEvents)
  const reconciliationMap = new Map(reconciliationUpdates.map((update) => [update.id, update.groupId]))

  const eventInput = {
    id: targetEvent.id,
    index: targetIndex,
    parents: targetParents,
    expectedVersion: targetEvent.version || undefined
  }
  if (conditionLabels !== undefined) {
    eventInput.conditionLabels = conditionLabels
  }
  if (reconciliationMap.has(targetEvent.id)) {
    eventInput.groupId = reconciliationMap.get(targetEvent.id)
  }

  const extraInputs = []
  const pushExtra = (id, extra) => {
    const index = domainEvents.findIndex((event) => event.id === id)
    if (index === -1) {
      return
    }
    const existing = extraInputs.find((input) => input.id === id)
    if (existing) {
      Object.assign(existing, extra)

      return
    }
    extraInputs.push({ id, index, expectedVersion: domainEvents[index].version || undefined, ...extra })
  }

  for (const parentId of targetParents) {
    if (parentId !== 'start') {
      pushExtra(parentId, {})
    }
  }
  for (const update of reconciliationUpdates) {
    if (update.id !== targetEvent.id) {
      pushExtra(update.id, { groupId: update.groupId })
    }
  }
  // Persist reordered parents for any other event the reflow moved (its primary parent changed),
  // so its column matches what the UI would render after the same edit.
  for (const event of reorderedEvents) {
    if (event.id === targetEvent.id) {
      continue
    }
    const originalParents = originalParentsById.get(event.id) || []
    const newParents = event.parents || []
    const parentsChanged = newParents.length !== originalParents.length ||
      newParents.some((parentId, index) => parentId !== originalParents[index])
    if (parentsChanged) {
      pushExtra(event.id, { parents: newParents })
    }
  }

  const eventsForSlotCalc = reorderedEvents.map((event) =>
    reconciliationMap.has(event.id) ? { ...event, groupId: reconciliationMap.get(event.id) } : event
  )
  const updatedGroups = recalculateGroupSlots(eventsForSlotCalc, groups)

  const stableOffsets = computeStableOffsets({
    domainEvents: eventsForSlotCalc,
    lanes: workflow.lanes,
    groups: updatedGroups || groups
  })
  for (const event of domainEvents) {
    const newOffset = stableOffsets[event.id]
    if (newOffset !== undefined && newOffset !== (event.offset || 0)) {
      if (event.id === targetEvent.id) {
        eventInput.offset = newOffset
      } else {
        pushExtra(event.id, { offset: newOffset })
      }
    }
  }

  const mutationInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    domainEvents: appendMissingParentInputs([eventInput, ...extraInputs], domainEvents),
    updateNote: {
      action,
      target: targetEvent.description || targetEvent.id
    }
  }
  if (updatedGroups) {
    mutationInput.groups = updatedGroups
  }

  return mutationInput
}

export const buildAddConnectionMutation = ({ workflow, workflowId, from, to, conditionLabel }) => {
  if (!from || !to) {
    return { error: 'Both "from" and "to" $ref paths are required' }
  }

  const sourceEvent = resolveDomainEventOrDecision(from, workflow)
  if (!sourceEvent) {
    return { error: `Source event or decision not found for ref: ${from}` }
  }

  const targetEvent = resolveDomainEventOrDecision(to, workflow)
  if (!targetEvent) {
    return { error: `Target event or decision not found for ref: ${to}` }
  }

  if (sourceEvent.id === targetEvent.id) {
    return { error: 'Cannot connect an event to itself' }
  }

  const domainEvents = workflow.domainEvents || []
  const targetIndex = domainEvents.findIndex((e) => e.id === targetEvent.id)
  if (targetIndex === -1) {
    return { error: 'Target event not found' }
  }

  if ((targetEvent.parents || []).includes(sourceEvent.id)) {
    return {
      error: `A connection from "${sourceEvent.description || from}" to "${targetEvent.description || to}" already exists`
    }
  }

  const eventMap = new Map(domainEvents.map((e) => [e.id, e]))
  const rawParents = [...(targetEvent.parents || []), sourceEvent.id]
  const parents = applyStartNodeRule(targetEvent.id, rawParents, eventMap)

  let conditionLabels
  // Only a decision branch carries a condition label; ignore it for other parents.
  if (conditionLabel && sourceEvent.type === 'bpmn:ExclusiveGateway') {
    const otherLabels = (targetEvent.conditionLabels || []).filter((cl) => cl.parentId !== sourceEvent.id)
    conditionLabels = [...otherLabels, { parentId: sourceEvent.id, name: conditionLabel }]
  }

  return {
    mutationInput: buildConnectionMutationInput({
      workflow,
      workflowId,
      targetEvent,
      targetIndex,
      parents,
      conditionLabels,
      action: 'added connection'
    }),
    sourceEvent,
    targetEvent,
    conditionLabels,
    message: `Connection added from "${sourceEvent.description || from}" to "${targetEvent.description || to}"${conditionLabels ? ` with label "${conditionLabel}"` : ''}`
  }
}

export const buildRemoveConnectionMutation = ({ workflow, workflowId, from, to }) => {
  if (!from || !to) {
    return { error: 'Both "from" and "to" $ref paths are required' }
  }

  const sourceEvent = resolveDomainEventOrDecision(from, workflow)
  if (!sourceEvent) {
    return { error: `Source event or decision not found for ref: ${from}` }
  }

  const targetEvent = resolveDomainEventOrDecision(to, workflow)
  if (!targetEvent) {
    return { error: `Target event or decision not found for ref: ${to}` }
  }

  const domainEvents = workflow.domainEvents || []
  const targetIndex = domainEvents.findIndex((e) => e.id === targetEvent.id)
  if (targetIndex === -1) {
    return { error: 'Target event not found' }
  }

  const eventMap = new Map(domainEvents.map((e) => [e.id, e]))

  if (!(targetEvent.parents || []).includes(sourceEvent.id)) {
    const decision = findDecisionBetween(sourceEvent.id, targetEvent, eventMap)
    if (decision) {
      const decisionRef = buildEventOrDecisionRef(decision, workflow)

      return {
        error: `No connection from "${sourceEvent.description || from}" to "${targetEvent.description || to}" exists. "${targetEvent.description || to}" follows the decision "${decision.description}", so remove ${decisionRef} -> ${to} instead.`
      }
    }

    return {
      error: `No connection from "${sourceEvent.description || from}" to "${targetEvent.description || to}" exists`
    }
  }

  const rawParents = (targetEvent.parents || []).filter((parentId) => parentId !== sourceEvent.id)
  const parents = applyStartNodeRule(targetEvent.id, rawParents, eventMap)

  const remainingLabels = (targetEvent.conditionLabels || []).filter((cl) => cl.parentId !== sourceEvent.id)
  const conditionLabels = remainingLabels.length ? remainingLabels : null

  const becameStart = parents[0] === 'start' && (targetEvent.parents || [])[0] !== 'start'

  return {
    mutationInput: buildConnectionMutationInput({
      workflow,
      workflowId,
      targetEvent,
      targetIndex,
      parents,
      conditionLabels,
      action: 'removed connection'
    }),
    sourceEvent,
    targetEvent,
    becameStart,
    message: `Connection removed from "${sourceEvent.description || from}" to "${targetEvent.description || to}"${becameStart ? `. "${targetEvent.description || targetEvent.id}" is now a starting point.` : ''}`
  }
}
